/**
 * PasswordResetModal — the fleet's one password reset: email → six-digit code → new password.
 *
 * Every tenant `<LoginShell>`, console, the marketing logins and planner-web's platform-admin
 * `<LoginForm>` open this modal, and each wires the four callbacks through
 * `usePasswordResetClient({ basePath })` (`web/require-login-shell-password-reset` refuses a mount
 * that does not). The state machine is `usePasswordResetFlow`; each step is its own component under
 * `./passwordReset/`, and each moves focus to its own field on arrival — the dialog's content is
 * REPLACED between steps, and focus left on a removed node falls to `<body>`.
 *
 * 🔴 UNREACHABLE-MODULE-BURNDOWN-001 L16 rebuilt this modal after deleting palm-web's app-local
 * reset tree, which had been unreachable for months with 11 green suites. What that tree had and
 * this lacked — a resend countdown synced to the server, a live requirement checklist, show/hide,
 * "use a different email", translated refusals, a step indicator, per-step focus — was built here
 * for every app at once. What this modal had and should never have had was deleted: a `simple`
 * flow and three no-callback fallbacks that REPORTED RESETS THAT NEVER HAPPENED, and a server
 * `redirectUrl` hop no reset route has ever answered with. See `usePasswordResetFlow` for each.
 *
 * @module @puffin-core/login-shell/components/PasswordResetModal
 */

import PropTypes from 'prop-types';
import {
  CaptchaField,
  IS_CAPTCHA_CONFIGURED,
  IS_DEVELOPMENT,
  deriveCaptchaRequired,
  useCaptchaToken,
} from '@puffin-core/captcha-ui';
// 🔴 The NARROW subpath, never the barrel. `@puffin-core/page-shell`'s index pulls
// `react-router-dom` (through `useManageAccountAction`), and login-shell is deliberately
// router-free — importing the barrel here dragged the router in and took 21 of this
// package's suites down with `ReferenceError: TextEncoder is not defined`.
import { Modal } from '@puffin-core/page-shell/modal';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';
import {
  usePasswordResetFlow,
  PasswordResetStep,
  DEFAULT_RESEND_COOLDOWN_SECONDS,
} from '../hooks/usePasswordResetFlow.js';
import { ResetEmailStep } from './passwordReset/ResetEmailStep.jsx';
import { ResetCodeStep } from './passwordReset/ResetCodeStep.jsx';
import { ResetNewPasswordStep } from './passwordReset/ResetNewPasswordStep.jsx';
import { ResetSuccess } from './passwordReset/ResetSuccess.jsx';

export { PasswordResetStep };

/**
 * Whether a request for a code needs a verified token: the ONE derivation sign-in's form uses (outside development a
 * token is required whether or not a site key is configured — fail closed).
 */
const IS_CAPTCHA_REQUIRED = deriveCaptchaRequired(IS_DEVELOPMENT, IS_CAPTCHA_CONFIGURED);

/**
 * The open dialog's body: the ONE captcha widget and the current step. The widget's token lives here, mounted only
 * while the dialog is open, so it survives the email → code step and a reopen starts clean.
 *
 * PLANNER-PALM-UAT-2026-11-03-001 finish gate 7c: the modal used to take the host's widget as an opaque node, so it
 * could not know whether the widget had verified, and "Send code" was live with no token. The modal now owns the
 * widget (the re-auth precedent) and holds both captcha-gated requests until it has a token, exactly as sign-in's
 * submit is held.
 *
 * @param {Object} props
 * @param {Object} props.flow - `usePasswordResetFlow`'s return
 * @param {Function} props.translate
 * @param {string} [props.captchaAction] - Turnstile's `action` for this reset's widget
 * @param {Function} props.onClose
 * @returns {React.JSX.Element}
 */
function ResetBody({ flow, translate, captchaAction, onClose }) {
  const token = useCaptchaToken();
  const captcha = {
    token: token.token,
    blocked: IS_CAPTCHA_REQUIRED && !token.token,
    reset: token.reset,
    field: <CaptchaField {...token.fieldProps} action={captchaAction} testId="reset-captcha" />,
  };
  return renderStep(flow, translate, captcha, onClose);
}

ResetBody.propTypes = {
  flow: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  captchaAction: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} [props.initialEmail] - pre-fills step 1 from the sign-in form
 * @param {() => (string|Promise<string>)} props.resolveSubdomain - the company the reset is for, asked when the code is
 *   requested (`<LoginForm>` hands its `resolveTenant`; a host that knows its company returns it)
 * @param {(email: string, subdomain: string, captchaToken: string|null) => Promise<any>} props.onInitiate
 * @param {(email: string, subdomain: string, code: string) => Promise<{resetToken: string}>} props.onVerify
 * @param {(resetToken: string, newPassword: string) => Promise<any>} props.onComplete
 * @param {(email: string, subdomain: string, captchaToken: string|null) => Promise<any>} props.onResend
 * @param {() => void} [props.onSuccess] - called once the new password is saved
 * @param {number} [props.resendCooldownSeconds=60] - the router's resend window; a refusal's
 *   `retryAfter` overrides it
 * @param {string} [props.captchaAction] - Turnstile's `action` for the reset's own widget, when the verifying router
 *   binds one (the owner console's `owner_console_password_reset`)
 * @param {Function} [props.t]
 * @returns {React.JSX.Element|null}
 */
export function PasswordResetModal({
  isOpen,
  onClose,
  initialEmail = '',
  resolveSubdomain,
  onInitiate,
  onVerify,
  onComplete,
  onResend,
  onSuccess,
  resendCooldownSeconds = DEFAULT_RESEND_COOLDOWN_SECONDS,
  captchaAction,
  t,
}) {
  const flow = usePasswordResetFlow({
    initialEmail,
    resolveSubdomain,
    onInitiate,
    onVerify,
    onComplete,
    onResend,
    onSuccess,
    resendCooldownSeconds,
  });
  const translate = useLoginTranslate(t);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpen={flow.handleOpen}
      title={translate('modal.passwordReset.title')}
      closeLabel={translate('modal.closeLabel')}
      testId="password-reset-modal"
    >
      <ResetBody
        flow={flow}
        translate={translate}
        captchaAction={captchaAction}
        onClose={onClose}
      />
    </Modal>
  );
}

/**
 * The body for the current step.
 *
 * @param {Object} flow
 * @param {Function} translate
 * @param {Object} captcha - `{ token, blocked, reset, field }`: the widget the two captcha-gated steps render
 * @param {Function} onClose
 * @returns {React.JSX.Element}
 */
function renderStep(flow, translate, captcha, onClose) {
  switch (flow.state.step) {
    case PasswordResetStep.CODE:
      return <ResetCodeStep flow={flow} translate={translate} captcha={captcha} />;
    case PasswordResetStep.NEW_PASSWORD:
      return <ResetNewPasswordStep flow={flow} translate={translate} />;
    case PasswordResetStep.SUCCESS:
      return <ResetSuccess onClose={onClose} translate={translate} />;
    default:
      return <ResetEmailStep flow={flow} translate={translate} captcha={captcha} />;
  }
}

PasswordResetModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialEmail: PropTypes.string,
  resolveSubdomain: PropTypes.func.isRequired,
  onInitiate: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  resendCooldownSeconds: PropTypes.number,
  captchaAction: PropTypes.string,
  t: PropTypes.func,
};

export default PasswordResetModal;
